import type React from 'react';
import { useCallback } from 'react';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import { deepEqual } from 'fast-equals';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { Trans, useTranslation } from 'react-i18next';
import { queryGovernor } from 'tods-competition-factory';

import { tournamentAtom } from 'atoms/tournament';
import StandardButton from 'components/buttons/StandardButton';
import TMXDialog from 'components/dialogs/TMXDialog';
import { RatingTypeEnum } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { BodyLarge, H3 } from 'components/typography/Typography';
import { formatDateTime } from 'utils/dateTimeUtils';

import { useStyles } from './styles';

export interface AutoSeedProps {
  event: any;
  loading?: boolean;
  open?: boolean;
  payload?: any;
  clearSeeds?: (disableToastMessage?: boolean) => Promise<void>;
  ratingType: RatingTypeEnum;
  onClose: () => void;
  onExited: () => void;
  onSave: () => void;
}

const AutoSeedDialog: React.FC<AutoSeedProps> = ({ event, loading, open, ratingType, onClose, onSave }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const paperProps = { style: { width: 500, padding: 30 } };

  const divisionData = event.extensions?.find((e) => e.name === 'division')?.value;
  const isFamily = !(typeof divisionData?.familyType === 'undefined' || divisionData?.familyType === null);
  const familyCategory = isFamily ? divisionData?.familyType : null;
  const categoryName = isFamily
    ? familyCategory
    : event?.category?.categoryName || event?.category?.ageCategoryCode || event?.eventId;
  const isWheelchair = event?.category?.subType === 'WHEELCHAIR';
  const isMixed = event?.gender === 'MIXED';
  const ranking = !ratingType;
  const itemEventType = isFamily ? 'TEAM' : 'SINGLES';
  const ratingItemType = `RETRIEVAL.RATING.${itemEventType}.${ratingType}`;

  const getRatingTimeItem = useCallback(
    (tournament) =>
      tournament &&
      (ratingType === RatingTypeEnum.WTN
        ? queryGovernor.getTimeItem({
            tournamentRecord: tournament,
            itemType: ratingItemType,
            element: event,
          })
        : queryGovernor.getTimeItem({
            itemType: ratingItemType,
            element: event,
          })),
    [event, ratingItemType, ratingType],
  );
  const ratingTimeItem = useAtomValue(selectAtom(tournamentAtom, getRatingTimeItem, deepEqual));
  const getRankingTimeItems = useCallback(
    (tournament) => {
      if (!tournament) return [];

      let rankingTimeItems;
      if (isMixed && ranking && !isWheelchair && !isFamily) {
        const rankingItemTypeMale = `RETRIEVAL.RANKING.SINGLES.MALE${categoryName}`;
        const rankingItemTypeFemale = `RETRIEVAL.RANKING.SINGLES.FEMALE${categoryName}`;

        const { timeItem: rankingTimeItemMale } = queryGovernor.getTimeItem({
          itemType: rankingItemTypeMale,
          element: event,
        });
        const { timeItem: rankingTimeItemFemale } = queryGovernor.getTimeItem({
          itemType: rankingItemTypeFemale,
          element: event,
        });

        rankingTimeItems = [
          { ...rankingTimeItemMale, name: t('mensPlural') },
          { ...rankingTimeItemFemale, name: t('womensPlural') },
        ];
      } else {
        const rankingItemType = `RETRIEVAL.RANKING.${itemEventType}.${categoryName}`;

        const { timeItem } = queryGovernor.getTimeItem({
          itemType: rankingItemType,
          element: event,
        });

        rankingTimeItems = [timeItem];
      }

      return rankingTimeItems.map((rankingTimeItem) => {
        const rankingAccessDate =
          rankingTimeItem && formatDateTime(rankingTimeItem.createdAt, { dateStyle: 'medium', timeStyle: 'short' });
        const ratingAccessDate =
          ratingTimeItem &&
          formatDateTime(ratingTimeItem?.timeItem?.createdAt, { dateStyle: 'medium', timeStyle: 'short' });
        const rankingPubDate =
          rankingTimeItem && formatDateTime(rankingTimeItem.itemValue, { dateStyle: 'medium', timeStyle: 'short' });
        const ratingPubDate =
          ratingTimeItem &&
          formatDateTime(ratingTimeItem?.timeItem?.itemValue, { dateStyle: 'medium', timeStyle: 'short' });

        return {
          name: rankingTimeItem?.name ?? rankingTimeItem?.itemType,
          rankingAccessDate,
          ratingAccessDate,
          rankingPubDate,
          ratingPubDate,
          whatUpdated: ranking ? rankingAccessDate : ratingAccessDate,
          whatPublished: ranking ? rankingPubDate : ratingPubDate,
        };
      });
    },
    [categoryName, event, isFamily, isMixed, isWheelchair, itemEventType, ranking, ratingTimeItem, t],
  );
  const rankingTimeItems = useAtomValue(selectAtom(tournamentAtom, getRankingTimeItems, deepEqual));

  const handleClose = () => {
    onClose();
  };
  const handleSave = () => {
    onSave();
    onClose();
  };
  const listsFetched = rankingTimeItems.every(
    ({ whatUpdated, whatPublished }) => (ranking && whatUpdated && whatPublished) || (!ranking && whatUpdated),
  );

  return (
    <TMXDialog fullWidth open={open} PaperProps={paperProps} onClose={handleClose}>
      <H3>{ranking ? t('autoseedRanking') : t('autoseedRating', { ratingType })}</H3>
      <DialogContent className={classes.dialogContent}>
        {listsFetched ? (
          <Grid container direction="column">
            <Grid item className={classes.spacing}>
              <BodyLarge>
                {ranking ? t('rankingSeedInfo', { categoryName }) : t('ratingSeedInfo', { ratingType })}
              </BodyLarge>
            </Grid>
            <Grid item className={classes.datesContainer}>
              {rankingTimeItems.map(({ name, whatUpdated, whatPublished }) => (
                <Trans
                  key={name}
                  components={{
                    desc: <BodyLarge />,
                  }}
                  i18nKey={ranking && rankingTimeItems.length > 1 ? 'rankingTimeItemMulti' : 'ratingUpdatesInfo'}
                  style={{ marginLeft: '10px' }}
                  values={{ name, updatedAt: whatUpdated, publishedAt: whatPublished }}
                />
              ))}
            </Grid>
            <Grid item className={classes.spacing}>
              <BodyLarge>{t('rankingsSeedMessage')}</BodyLarge>
            </Grid>
          </Grid>
        ) : (
          <Grid container direction="column">
            {ranking ? t('rankingsNotRetrieved') : t('ratingsNotRetrieved')}
          </Grid>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <StandardButton onClick={handleClose}>{t('cancel')}</StandardButton>
        {listsFetched && (
          <StandardButton loading={loading} variant="primary" onClick={handleSave}>
            {t('continueText')}
          </StandardButton>
        )}
      </DialogActions>
    </TMXDialog>
  );
};

export default AutoSeedDialog;
